import { CircleDot, MoonStar } from '@orgus/ui/icons';
import type { LucideIcon } from '@orgus/ui/icons';
import { useNavigate } from 'react-router';
import { Button, Spinner, Tooltip, cn, formatClock, formatDuration } from '@orgus/ui';
import { barPieces, noActivityReason, usableActivity } from './activity.ts';
import { MARK_ICON, burstsText, markText } from './ActivityBar.tsx';
import type { TimelineRun } from './model.ts';

type Row = {
  at: number;
  key: string;
  icon: LucideIcon;
  iconClass: string;
  text: string;
  /** What resumed the burst: on its icon, on hover, never a clause repeated on every row. */
  note: string | null;
  /** The idle gap after a burst, as a quiet caption inside its row ("then 22m idle"). */
  idle: string | null;
  detail: string;
  tone: 'work' | 'quiet' | 'mark';
};

const PIECE: Record<'active' | 'quiet', { icon: LucideIcon; iconClass: string; label: string }> = {
  active: { icon: CircleDot, iconClass: 'text-status-working', label: 'Working' },
  quiet: { icon: MoonStar, iconClass: 'text-fg-subtle', label: 'Quiet since' },
};

/**
 * A span's length as its clock times read, once past a minute: 17:53:25 to 18:53:04 shows as
 * "17:53 to 18:53", so it reads 1 h 00 min, not 59 min, and a burst and the idle gap after it add up.
 */
export function clockSpan(from: number, to: number): string {
  if (to - from < 60_000) return formatDuration(Math.max(0, to - from));
  return formatDuration(Math.floor(to / 60_000) * 60_000 - Math.floor(from / 60_000) * 60_000);
}

/** A resume this close to a burst's start is what opened it. */
const OPENS_MS = 60_000;

/**
 * Bursts (each with what resumed it and the idle gap after it), a quiet stretch at the end of
 * a running run, and the marks inside a burst (compactions, resets), oldest first. Idle gaps
 * are no rows of their own: they would double the list.
 */
export function activityRows(run: TimelineRun, now: number): Row[] {
  const a = usableActivity(run);
  if (!a) return [];
  const used = new Set<number>();
  const rows: Row[] = [];
  barPieces(run, now).forEach((p, i) => {
    if (p.kind === 'idle') {
      const prev = rows[rows.length - 1];
      if (prev && prev.tone === 'work') prev.idle = clockSpan(p.from, p.to);
      return;
    }
    const k = PIECE[p.kind];
    const live = p.kind === 'active' && run.status === 'running' && p.to >= now - 1000;
    let note: string | null = null;
    if (p.kind === 'active') {
      const opener = a.marks.findIndex((m, j) => !used.has(j) && m.kind === 'resume' && Math.abs(m.at - p.from) <= OPENS_MS);
      if (opener >= 0) {
        used.add(opener);
        note = markText(a.marks[opener]!);
      }
    }
    rows.push({
      at: p.from,
      key: `p${i}`,
      icon: note ? MARK_ICON.resume.icon : k.icon,
      iconClass: note ? MARK_ICON.resume.className : k.iconClass,
      text: p.kind === 'quiet' ? `${k.label} ${formatClock(p.from)}` : `${formatClock(p.from)} to ${live ? 'now' : formatClock(p.to)}`,
      note,
      idle: null,
      detail: clockSpan(p.from, p.to),
      tone: p.kind === 'active' ? 'work' : 'quiet',
    });
  });
  a.marks.forEach((m, i) => {
    if (used.has(i)) return;
    const { icon, className } = MARK_ICON[m.kind];
    rows.push({ at: m.at, key: `m${i}`, icon, iconClass: className, text: markText(m), note: null, idle: null, detail: formatClock(m.at), tone: 'mark' });
  });
  return rows.sort((x, y) => x.at - y.at || (x.tone === 'mark' ? 1 : 0) - (y.tone === 'mark' ? 1 : 0));
}

/** Why a run shows no bursts, in a few words. */
export function noActivityText(run: Pick<TimelineRun, 'status' | 'startedAt' | 'endedAt'>, now: number): string {
  return noActivityReason(run, now) === 'old' ? 'Not read for runs over a day old' : 'Not reported by its machine';
}

/**
 * One quiet line for activity a machine does not report, with the way to fix it: update the
 * machine's agent, on its page. Said once per drawer, never once per agent.
 */
export function ActivityUnreported({ machineId, text }: { machineId: string | null; text: string }) {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-control-sm items-center gap-2 text-caption text-fg-subtle">
      <span className="min-w-0 truncate">{text}</span>
      {machineId && (
        <Button variant="ghost" size="sm" className="-mr-2 ml-auto shrink-0" onClick={() => navigate(`/machines?machine=${encodeURIComponent(machineId)}`)}>
          Update its agent
        </Button>
      )}
    </div>
  );
}

/**
 * When a subagent or agent actually worked: its bursts on one quiet surface, each with the idle
 * gap after it and what resumed it on its icon. `unreported` is where a run with no activity
 * says so; `null` leaves it to the caller (a workflow says it once for all its agents).
 */
export function ActivityLog({ run, now, machineId = null, unreported = 'line' }: { run: TimelineRun; now: number; machineId?: string | null; unreported?: 'line' | 'none' }) {
  if (run.kind === 'workflow') return null;
  const a = run.activity;
  if (!a && unreported === 'none') return null;
  const summary = burstsText(run, now);
  return (
    <section aria-label="Activity" className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3 text-caption">
        <span className="font-medium text-fg-muted">Activity</span>
        {summary && <span className="truncate text-fg-subtle num">{summary}</span>}
      </div>
      {!a ? (
        <ActivityUnreported machineId={noActivityReason(run, now) === 'old' ? null : machineId} text={noActivityText(run, now)} />
      ) : a.partial ? (
        <p className="flex items-center gap-2 text-caption text-fg-subtle">
          <Spinner label="Reading its transcript" />
          Reading its transcript
        </p>
      ) : (
        <ol className="flex flex-col rounded-card bg-sunken py-1">
          {activityRows(run, now).map((r) => {
            const Icon = r.icon;
            const icon = <Icon className={cn('size-4 shrink-0', r.iconClass)} aria-hidden={!r.note} aria-label={r.note ?? undefined} />;
            return (
              <li key={r.key} className="flex h-control items-center gap-2 px-3 text-ui">
                {r.note ? (
                  <Tooltip content={r.note}>
                    <span className="flex shrink-0">{icon}</span>
                  </Tooltip>
                ) : (
                  icon
                )}
                <span className={cn('shrink-0 num', r.tone === 'work' ? 'text-fg-muted' : 'text-fg-subtle')}>{r.text}</span>
                {r.idle && <span className="min-w-0 truncate text-caption text-fg-subtle num">then {r.idle} idle</span>}
                <span className="ml-auto shrink-0 text-caption text-fg-subtle num">{r.detail}</span>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
